import { GRID_COLUMNS, createSectionColumnId, equalColumnSpans } from '../utils/proposalSectionColumns';
import { DEFAULT_SECTION_MARGIN_BOTTOM_CSS } from '../utils/proposalUnits';
import { COMPOSITION_LAYOUTS, COMPOSITION_TILE_TYPES } from './compositionLayouts';
import {
  CLIPBOARD_SECTION_TILES, collectBlockSubtree, instantiateClipboardBlock,
} from './blockClipboard';

const DEFAULT_PAGE_BACKGROUND = '#ffffff';

/** Ancien défaut éditeur : padding container section (retiré). */
const LEGACY_SECTION_PADDING_PX = 16;

/**
 * Blocs « contenu proposal » : doivent vivre dans une colonne de ProposalSection
 * (comme à l’import proposal). Si ajoutés à la racine, on crée la section automatiquement.
 */
export const PROPOSAL_ZONE_BLOCK_TYPES = new Set([
  'ProposalRichText',
  'ProposalTitle',
  'ProposalImage',
  'ProposalShape',
  'EditableDataTable',
  'ProposalCheckbox',
  'ProposalSelector',
  // Vidéo : bloc web, mais même règle que l'Image — un média nu à la racine
  // est wrappé dans un Bloc 1 colonne (symétrie Image/Vidéo).
  'VideoBlock',
  'ImageComparisonBlock',
  'HotspotImageBlock',
  'ScrollStoryBlock',
  // Contenu externe (Calendly, Loom, Figma…) : même règle, c'est une box.
  'EmbedBlock',
  // Carte info (chiffres clés) : même règle — la box vit dans un Bloc.
  'InfoCard',
  // Volet question/réponse : même règle.
  'AccordionPanel',
  // Box web : une box ne se pose jamais nue sur la page, c'est le Bloc qui
  // porte la disposition (colonnes, fond, visibilité, animation).
  'TimelineBlock',
  'CarouselBlock',
  'SectionDivider',
  'Spacer',
  'ButtonBlock',
]);

function normalizeProposalSectionProps(props) {
  if (!props || typeof props !== 'object') return props;
  const next = { ...props };
  if (next.paddingTop === LEGACY_SECTION_PADDING_PX) next.paddingTop = 0;
  if (next.paddingBottom === LEGACY_SECTION_PADDING_PX) next.paddingBottom = 0;
  return next;
}

function normalizeEditableDataTableProps(props) {
  if (!props || typeof props !== 'object') return props;
  const next = { ...props };
  delete next.proposalWidthPx;
  delete next.useProposalContentWidth;
  delete next.alignHorizontal;
  return next;
}

/** Blocs portant le champ « Alignement vertical » (colonne étirée). */
const VERTICAL_ALIGN_BLOCK_TYPES = new Set([
  'ProposalRichText', 'ProposalImage', 'InfoCard', 'AccordionPanel',
  'TimelineBlock', 'ButtonBlock',
]);
/** Blocs dont l'horizontal est aussi backfillé (pas posé par le parser). */
const HORIZONTAL_ALIGN_BLOCK_TYPES = new Set([
  'InfoCard', 'AccordionPanel', 'TimelineBlock', 'ButtonBlock',
]);

function normalizePuckBlock(item) {
  if (!item || typeof item !== 'object') return item;
  if (item.type === 'EditableDataTable') {
    return { ...item, props: normalizeEditableDataTableProps(item.props) };
  }
  // Backfill : la prop doit exister pour que le sélecteur de la rightbar
  // affiche la valeur effective (« Centre ») même sans choix utilisateur.
  if (VERTICAL_ALIGN_BLOCK_TYPES.has(item.type)) {
    const patch = {};
    if (item.props?.alignVertical == null) patch.alignVertical = 'center';
    if (HORIZONTAL_ALIGN_BLOCK_TYPES.has(item.type) && item.props?.alignHorizontal == null) {
      patch.alignHorizontal = 'center';
    }
    if (Object.keys(patch).length) {
      return { ...item, props: { ...item.props, ...patch } };
    }
  }
  return item;
}

function normalizeProposalPuckContent(content) {
  if (!Array.isArray(content)) return [];
  return content.map((item) => {
    if (!item || typeof item !== 'object') return item;
    if (item.type === 'ProposalSection' || item.type === 'ProposalFreeLayout') {
      return {
        ...item,
        props: normalizeProposalSectionProps(item.props),
      };
    }
    return normalizePuckBlock(item);
  });
}

function normalizeProposalPuckZones(zones) {
  if (!zones || typeof zones !== 'object') return zones;
  return Object.fromEntries(
    Object.entries(zones).map(([key, blocks]) => [
      key,
      Array.isArray(blocks) ? blocks.map(normalizePuckBlock) : blocks,
    ]),
  );
}

function sectionIdSuffixFromBlock(item) {
  const rawId = item.props?.id;
  if (typeof rawId === 'string' && rawId.trim()) {
    return rawId
      .replace(/^box-/, '')
      .replace(/^(table-|text-|title-|image-|shape-|checkbox-)/, '');
  }
  return createSectionColumnId();
}

function sectionTypeSlug(blockType) {
  const map = {
    ProposalRichText: 'text',
    ProposalTitle: 'title',
    ProposalImage: 'image',
    ProposalShape: 'shape',
    EditableDataTable: 'table',
    ProposalCheckbox: 'checkbox',
    ProposalSelector: 'selector',
    InfoCard: 'info',
    AccordionPanel: 'panel',
    TimelineBlock: 'timeline',
    CarouselBlock: 'carousel',
    SectionDivider: 'divider',
    Spacer: 'spacer',
    ButtonBlock: 'button',
    EmbedBlock: 'embed',
    ImageComparisonBlock: 'comparison',
    HotspotImageBlock: 'hotspots',
    ScrollStoryBlock: 'story',
  };
  return map[blockType] || 'block';
}

function wrapBlockInProposalSection(item, zones, usedIds) {
  const colId = createSectionColumnId();
  const suffix = sectionIdSuffixFromBlock(item);
  let sectionId = `section-${sectionTypeSlug(item.type)}-${suffix}`;
  // L'id dérivé de la box n'est unique que tant qu'elle n'a jamais été
  // wrappée : une box sortie de son Bloc auto puis re-droppée à la racine
  // recréerait le même id (doublon de key React). Collision → id aléatoire.
  if (usedIds?.has(sectionId)) {
    sectionId = `section-${sectionTypeSlug(item.type)}-${createSectionColumnId()}`;
  }
  usedIds?.add(sectionId);
  const zoneKey = `${sectionId}:col-${colId}`;

  zones[zoneKey] = [normalizePuckBlock(item)];

  return {
    type: 'ProposalSection',
    props: normalizeProposalSectionProps({
      id: sectionId,
      backgroundColor: '',
      paddingTop: 0,
      paddingBottom: 0,
      marginBottom: DEFAULT_SECTION_MARGIN_BOTTOM_CSS,
      isVisible: true,
      sectionColumns: {
        preset: 'row',
        columnIds: [colId],
        columnSpans: [],
      },
    }),
  };
}

/**
 * L'action Puck est-elle un drop de bloc contenu directement à la racine ?
 * (déclencheur de l'auto-création de Section — voir PuckPageEditor.onAction).
 */
export function isRootProposalInsert(action) {
  return action?.type === 'insert'
    && (action.destinationZone === 'root:default-zone' || action.destinationZone === 'default-zone')
    && PROPOSAL_ZONE_BLOCK_TYPES.has(action.componentType);
}

const isColumnZone = (zone) => typeof zone === 'string' && zone.includes(':col-');

/**
 * L'action Puck est-elle la duplication d'une box dans une colonne de
 * section ? (règle « 1 box par colonne » : la copie ne peut pas rester dans
 * la colonne — elle part dans son propre Bloc, sous le Bloc parent).
 */
export function isColumnBoxDuplicate(action) {
  return action?.type === 'duplicate' && isColumnZone(action.sourceZone);
}

/**
 * Déplace la copie fraîchement dupliquée (état post-action : à
 * `sourceIndex + 1` dans `zoneKey`) vers un nouveau Bloc 1 colonne inséré
 * juste sous le Bloc parent.
 * @returns {{data: object, selection: {zone: string, index: number}|null}}
 */
export function moveDuplicatedBoxToNewSection(puckData, zoneKey, sourceIndex) {
  const zoneContent = puckData?.zones?.[zoneKey];
  const copy = Array.isArray(zoneContent) ? zoneContent[sourceIndex + 1] : null;
  if (!copy) return { data: puckData, selection: null };

  const content = Array.isArray(puckData.content) ? puckData.content : [];
  const parentSectionId = zoneKey.split(':')[0];
  const parentIndex = content.findIndex((item) => item?.props?.id === parentSectionId);
  if (parentIndex === -1) return { data: puckData, selection: null };

  const zones = {
    ...puckData.zones,
    [zoneKey]: zoneContent.filter((_, i) => i !== sourceIndex + 1),
  };
  const usedIds = new Set(content.map((item) => item?.props?.id).filter(Boolean));
  const section = wrapBlockInProposalSection(copy, zones, usedIds);

  const nextContent = [...content];
  nextContent.splice(parentIndex + 1, 0, section);

  // Zone unique du Bloc créé (id de section unique via usedIds).
  const newZoneKey = Object.keys(zones).find(
    (key) => key.startsWith(`${section.props.id}:col-`),
  ) || null;
  return {
    data: { ...puckData, content: nextContent, zones },
    selection: newZoneKey ? { zone: newZoneKey, index: 0 } : null,
  };
}

/**
 * L'action Puck est-elle le déplacement d'une box existante vers la racine ?
 * (drag d'une box hors de sa colonne, lâchée entre deux Blocs). L'action
 * `move` ne porte pas componentType : on lit le bloc déplacé dans l'état
 * post-action (`data` = appState.data reçu par onAction).
 */
export function isRootProposalMove(action, data) {
  if (action?.type !== 'move') return false;
  if (action.destinationZone !== 'root:default-zone' && action.destinationZone !== 'default-zone') {
    return false;
  }
  const item = data?.content?.[action.destinationIndex];
  return !!item?.type && PROPOSAL_ZONE_BLOCK_TYPES.has(item.type);
}

/**
 * Bloc contenu proposal à la racine → section 1 colonne + dropzone (comme
 * import proposal). Appliqué au chargement (ensurePagePuckRoot) et au drop
 * racine dans l'éditeur (onAction insert → setData wrappé) : l'utilisateur
 * droppe un Texte entre deux sections, la Section se crée toute seule.
 */
export function wrapRootProposalBlocksInSection(puckData) {
  const content = puckData?.content;
  if (!Array.isArray(content) || content.length === 0) return puckData;

  let changed = false;
  const zones = puckData.zones && typeof puckData.zones === 'object'
    ? { ...puckData.zones }
    : {};
  const usedIds = new Set(
    content.map((item) => item?.props?.id).filter(Boolean),
  );
  const nextContent = [];

  for (const item of content) {
    if (item?.type && PROPOSAL_ZONE_BLOCK_TYPES.has(item.type)) {
      nextContent.push(wrapBlockInProposalSection(item, zones, usedIds));
      changed = true;
    } else {
      nextContent.push(item);
    }
  }

  if (!changed) return puckData;
  return { ...puckData, content: nextContent, zones };
}

/**
 * Où se trouve un bloc, au format attendu par `setUi.itemSelector`.
 *
 * Après le wrap, la box quitte la racine pour la colonne du Bloc créé : sa
 * position d'avant appartient désormais au Bloc, et interroger l'index de Puck
 * trop tôt rendait justement celle-là. On lit donc la position dans les données
 * qu'on vient d'écrire, sans rien attendre.
 *
 * Rend `null` pour un bloc resté à la racine : Puck sait déjà le sélectionner.
 */
export function zoneSelectorForBlockId(puckData, id) {
  if (!id) return null;
  const zones = puckData?.zones || {};
  for (const [zone, items] of Object.entries(zones)) {
    const index = (items || []).findIndex((item) => item?.props?.id === id);
    if (index >= 0) return { index, zone };
  }
  return null;
}

/** @deprecated utiliser {@link wrapRootProposalBlocksInSection} */
export const wrapRootEditableDataTablesInSection = wrapRootProposalBlocksInSection;

/**
 * Tuiles palette « créatrices de disposition » (macros jamais persistées) :
 * elles n'insèrent pas une box mais génèrent un Bloc complet pré-rempli.
 * Elles ne peuvent donc PAS être droppées dans une colonne — uniquement
 * entre deux Blocs (racine), cf. disallow des DropZones de colonne
 * (ProposalSection / ProposalSectionNestedGrid).
 */
export const LAYOUT_MACRO_BLOCK_TYPES = COMPOSITION_TILE_TYPES;

/**
 * Ce qu'une colonne de Bloc refuse : les macros (elles génèrent un Bloc, pas
 * une box) et le bloc Onglets (il HÉBERGE des Blocs — le mettre dans la
 * colonne d'un Bloc emboîterait des dispositions sans fin).
 */
export const COLUMN_DISALLOWED_BLOCK_TYPES = [
  ...LAYOUT_MACRO_BLOCK_TYPES,
  'TabsBlock',
  // La copie d'un Bloc pose un Bloc : même raison que les macros. La copie
  // d'une BOX, elle, est la bienvenue dans une colonne — c'est tout l'intérêt.
  ...CLIPBOARD_SECTION_TILES,
];
/**
 * Ce qu'une colonne de Bloc accepte : tout ce que la config connaît, sauf la
 * liste ci-dessus.
 *
 * Une liste BLANCHE, et non le refus direct de `COLUMN_DISALLOWED_BLOCK_TYPES` :
 * les compositions enregistrées créent des types à la volée, qu'aucune liste
 * écrite d'avance ne peut nommer. Refuser tout ce qui n'est pas une box connue
 * les tient dehors sans avoir à les énumérer.
 */
export function columnAllowedBlockTypes(config) {
  return Object.keys(config?.components || {})
    .filter((type) => !COLUMN_DISALLOWED_BLOCK_TYPES.includes(type));
}

/**
 * Drop d'une tuile macro à la racine ? Déclencheur de l'expansion
 * (voir PuckPageEditor.onAction). Uniquement à l'insertion — les colonnes
 * refusent ces macros (disallow des DropZones).
 * @returns {null|{componentType: string, index: number}}
 */
export function getLayoutMacroInsert(action) {
  if (action?.type !== 'insert') return null;
  if (!LAYOUT_MACRO_BLOCK_TYPES.includes(action.componentType)) return null;
  const root = action.destinationZone === 'root:default-zone'
    || action.destinationZone === 'default-zone';
  if (!root) return null;
  return { componentType: action.componentType, index: action.destinationIndex };
}

/**
 * Construit un Bloc à partir d'une disposition décrite, et remplace par lui le
 * bloc posé à `index`.
 *
 * Toutes les tuiles créatrices de disposition passent par ici — les macros à
 * contenu de démo comme les compositions à box vides. Chacune recopiait sinon
 * le même littéral `{ type: 'ProposalSection', props: { … } }` et la même
 * fabrication de zones, à sept exemplaires.
 *
 * La disposition est décrite avec le vocabulaire du catalogue partagé
 * (`src/shared/compositions/`) : une colonne porte `box` OU `rows`, et `span`
 * s'exprime en douzièmes. Une colonne à `rows` fait basculer tout le Bloc en
 * grille imbriquée, la seule forme qui sache empiler des cellules.
 *
 * @param {object} puckData
 * @param {number} index position du bloc à remplacer
 * @param {object} composition
 * @param {string} composition.expectedType type attendu à `index` (garde-fou)
 * @param {string} composition.slug préfixe parlant de l'identifiant du Bloc
 * @param {Array} composition.columns colonnes, cellule = `{ span, box }`
 * @param {object} [composition.sectionProps] props du Bloc à écraser (fond, marges)
 * @returns {object} `puckData` lui-même si rien n'est à faire
 */
export function buildSectionFromComposition(puckData, index, {
  expectedType, slug, columns, sectionProps = {},
}) {
  const content = puckData?.content;
  if (!Array.isArray(content)) return puckData;
  const item = content[index];
  if (!item || (expectedType && item.type !== expectedType)) return puckData;
  if (!Array.isArray(columns) || columns.length === 0) return puckData;

  const zones = puckData.zones && typeof puckData.zones === 'object'
    ? { ...puckData.zones }
    : {};
  const sectionId = `section-${slug}-${createSectionColumnId()}`;

  // Les cellules, dans l'ordre de lecture : c'est `columnIds`, la liste que le
  // reste du code (verrous, duplication, alignements) parcourt.
  const cellIds = [];
  const addCell = (cell) => {
    const id = createSectionColumnId();
    cellIds.push(id);
    if (cell?.box) zones[`${sectionId}:col-${id}`] = [cell.box];
    return id;
  };

  const isNested = columns.some((column) => column?.rows?.length);

  const sectionColumns = isNested
    ? {
      preset: 'nested',
      columnIds: cellIds,
      columnSpans: [],
      nested: {
        mainColumnSpans: partialSpansFromTwelfths(columns),
        mainColumns: columns.map((column) => ({
          id: createSectionColumnId(),
          // Une colonne sans lignes tient dans une ligne d'une cellule : la
          // grille imbriquée est la forme du Bloc entier, pas d'une colonne.
          rows: (column?.rows?.length ? column.rows : [{ columns: [column] }])
            .map((row) => {
              const rowColumns = row?.columns?.length ? row.columns : [{}];
              return {
                id: createSectionColumnId(),
                columnSpans: partialSpansFromTwelfths(rowColumns),
                columns: rowColumns.map((cell) => ({ id: addCell(cell) })),
              };
            }),
        })),
      },
    }
    : {
      preset: 'row',
      columnIds: columns.map((column) => addCell(column)),
      columnSpans: partialSpansFromTwelfths(columns),
    };

  const section = {
    type: 'ProposalSection',
    props: {
      id: sectionId,
      backgroundColor: '',
      paddingTop: 0,
      paddingBottom: 0,
      marginBottom: DEFAULT_SECTION_MARGIN_BOTTOM_CSS,
      isVisible: true,
      ...sectionProps,
      sectionColumns,
    },
  };

  const nextContent = [...content];
  nextContent[index] = section;
  return { ...puckData, content: nextContent, zones };
}

/**
 * Largeurs en douzièmes → `columnSpans`, qui n'en porte que les n-1 premières
 * (la dernière colonne prend ce qui reste). Sous `md`, chaque colonne repasse
 * pleine largeur : côte à côte sur un téléphone, aucune ne serait lisible.
 */
function partialSpansFromTwelfths(columns) {
  if (!Array.isArray(columns) || columns.length <= 1) return [];
  return columns
    .slice(0, -1)
    .map((column) => ({ base: GRID_COLUMNS, md: column?.span ?? Math.floor(GRID_COLUMNS / columns.length) }));
}

/**
 * Pose la disposition de la tuile `componentType` à la place du bloc inséré à
 * `index`. Les fabriques de disposition vivent dans `compositionLayouts.js` :
 * ici ne reste que le branchement sur le modèle de page.
 *
 * @returns {object} `puckData` lui-même si rien n'est à faire
 */
export function expandCompositionAtIndex(puckData, index, componentType) {
  const layout = COMPOSITION_LAYOUTS[componentType];
  if (!layout) return puckData;
  return buildSectionFromComposition(puckData, index, {
    expectedType: componentType,
    ...layout(),
  });
}

/* Les tuiles nommées : mêmes signatures qu'avant, pour les appelants et les
   tests qui visent une macro en particulier. */
export const expandMediaTextBlockAtIndex = (puckData, index) => (
  expandCompositionAtIndex(puckData, index, puckData?.content?.[index]?.type)
);
export const expandLogoStripBlockAtIndex = (puckData, index) => expandCompositionAtIndex(puckData, index, 'LogoStripBlock');
export const expandKeyFiguresBlockAtIndex = (puckData, index) => expandCompositionAtIndex(puckData, index, 'KeyFiguresBlock');
export const expandFaqBlockAtIndex = (puckData, index) => expandCompositionAtIndex(puckData, index, 'FaqBlock');
export const expandHeroBlockAtIndex = (puckData, index) => expandCompositionAtIndex(puckData, index, 'HeroBlock');
export const expandActionBannerBlockAtIndex = (puckData, index) => expandCompositionAtIndex(puckData, index, 'ActionBannerBlock');


/* ── Répéteur de colonnes ─────────────────────────────────────────────────── */

/**
 * Au-delà, la grille de 12 ne se partage plus en colonnes lisibles et le Bloc
 * n'est plus une rangée mais une liste — qui se fait autrement.
 */
/**
 * Plafond de colonnes d'une rangée. Au-delà, sur la largeur d'une page, chaque
 * colonne devient trop étroite pour porter quoi que ce soit de lisible.
 */
export const MAX_SECTION_COLUMNS = 4;

const COLUMN_ZONE_SEPARATOR = ':col-';

function findSectionIndex(content, sectionId) {
  return (Array.isArray(content) ? content : [])
    .findIndex((item) => item?.type === 'ProposalSection' && item?.props?.id === sectionId);
}

/**
 * La colonne de Bloc qui porte cette box, ou `null`.
 *
 * Sert de condition au « + » de la barre d'outils d'une box : rien n'est
 * déclaré nulle part, aucun bloc n'est marqué comme duplicable, la propriété se
 * lit sur les données. Toute box posée dans une rangée en bénéficie.
 *
 * `null` sur une grille imbriquée (Bannière d'accueil, Bannière d'action) : ses
 * cellules ne sont pas les colonnes d'une rangée, « la colonne d'à côté » n'y
 * veut rien dire.
 */
export function findBoxColumn(puckData, boxId) {
  if (!boxId) return null;
  const zones = puckData?.zones || {};
  const key = Object.keys(zones).find((zoneKey) => (
    zoneKey.includes(COLUMN_ZONE_SEPARATOR)
    && (zones[zoneKey] || []).some((box) => box?.props?.id === boxId)
  ));
  if (!key) return null;

  const at = key.indexOf(COLUMN_ZONE_SEPARATOR);
  const sectionId = key.slice(0, at);
  const columnId = key.slice(at + COLUMN_ZONE_SEPARATOR.length);

  const sectionIndex = findSectionIndex(puckData.content, sectionId);
  if (sectionIndex < 0) return null;
  const columns = puckData.content[sectionIndex].props?.sectionColumns;
  if (columns?.preset !== 'row') return null;

  const columnIds = Array.isArray(columns.columnIds) ? columns.columnIds : [];
  const position = columnIds.indexOf(columnId);
  if (position < 0) return null;

  return {
    sectionId, columnId, columnIds, position, sectionIndex,
  };
}

/** Le « + » a-t-il lieu d'être sur cette box ? */
export function canDuplicateBoxColumn(puckData, boxId) {
  const found = findBoxColumn(puckData, boxId);
  return !!found && found.columnIds.length < MAX_SECTION_COLUMNS;
}

/**
 * Duplique une box dans une colonne neuve, insérée immédiatement à sa droite,
 * et rééquilibre les largeurs.
 *
 * Les identifiants sont renouvelés de bout en bout par le presse-papier
 * (`instantiateClipboardBlock`) : deux box partageant un id donneraient deux
 * clés React identiques et un verrou de collaboration commun.
 *
 * No-op par identité référentielle quand la box ne s'y prête pas — une copie
 * égale relancerait un rendu et un patch d'autosave pour rien.
 */
export function duplicateBoxIntoNewColumn(puckData, boxId) {
  const found = findBoxColumn(puckData, boxId);
  if (!found || found.columnIds.length >= MAX_SECTION_COLUMNS) return puckData;

  const {
    sectionId, columnId, columnIds, position, sectionIndex,
  } = found;
  const sourceBox = (puckData.zones[`${sectionId}${COLUMN_ZONE_SEPARATOR}${columnId}`] || [])
    .find((box) => box?.props?.id === boxId);
  if (!sourceBox) return puckData;

  const clone = instantiateClipboardBlock({
    item: sourceBox,
    zones: collectBlockSubtree(puckData.zones, boxId),
  });
  if (!clone) return puckData;

  const newColumnId = createSectionColumnId();
  // Juste à droite de la colonne d'origine, pas en fin de rangée : c'est là que
  // l'utilisateur vient de cliquer, et c'est là qu'il attend la copie.
  const nextColumnIds = [...columnIds];
  nextColumnIds.splice(position + 1, 0, newColumnId);

  const section = puckData.content[sectionIndex];
  const nextContent = [...puckData.content];
  nextContent[sectionIndex] = {
    ...section,
    props: {
      ...section.props,
      sectionColumns: {
        ...section.props.sectionColumns,
        columnIds: nextColumnIds,
        columnSpans: equalColumnSpans(nextColumnIds.length),
      },
    },
  };

  return {
    ...puckData,
    content: nextContent,
    zones: {
      ...puckData.zones,
      ...clone.zones,
      [`${sectionId}${COLUMN_ZONE_SEPARATOR}${newColumnId}`]: [clone.item],
    },
  };
}

export function getPuckRootProps(puckData) {
  const root = puckData?.root;
  if (!root || typeof root !== 'object') return {};
  if (root.props && typeof root.props === 'object') return root.props;
  return root;
}

/** Aligne puckData.root avec le titre de page (navigation / onglets). */
export function ensurePagePuckRoot(puckData, page = {}) {
  const base = puckData && typeof puckData === 'object'
    ? puckData
    : { content: [], root: {} };
  const root = base.root && typeof base.root === 'object' ? base.root : {};
  const current = getPuckRootProps(base);
  const nextProps = {
    backgroundColor: current.backgroundColor ?? DEFAULT_PAGE_BACKGROUND,
    title: current.title ?? page.title ?? '',
  };

  const normalized = {
    ...base,
    content: normalizeProposalPuckContent(base.content),
    ...(base.zones ? { zones: normalizeProposalPuckZones(base.zones) } : {}),
    root: {
      ...root,
      props: nextProps,
    },
  };

  return wrapRootProposalBlocksInSection(normalized);
}

export function extractPageTitleFromPuckData(puckData) {
  const title = getPuckRootProps(puckData).title;
  return typeof title === 'string' ? title.trim() : '';
}

export { DEFAULT_PAGE_BACKGROUND };
